import React from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../Footer';
import useOngoingProjects from '../../hooks/useOngoingProjects';
import './OngoingProjectsList.css';

function OngoingProjectsList() {
  const navigate = useNavigate();
  
  // Use the custom hook to fetch ongoing projects from Supabase
  const { projects, loading, error } = useOngoingProjects();

  const handleBackClick = () => {
    navigate('/projects');
  };

  if (loading) {
    return (
      <>
        <div className="ongoing-projects-list-page">
          <div className="header-banner">
            <div className="wall-container">
              <video className="bg-video" autoPlay muted loop playsInline>
                <source src={process.env.PUBLIC_URL + '/videos/drone-video.mp4'} />
              </video>
            </div>
            <h1>Ongoing Projects</h1>
          </div>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading projects...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="ongoing-projects-list-page">
          <div className="header-banner">
            <div className="wall-container">
              <video className="bg-video" autoPlay muted loop playsInline>
                <source src={process.env.PUBLIC_URL + '/videos/drone-video.mp4'} />
              </video>
            </div>
            <h1>Ongoing Projects</h1>
          </div>
          <div className="error-container">
            <p>Failed to load projects: {error.message}</p>
            <button onClick={handleBackClick} className="back-button">
              ← Back to Projects
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="ongoing-projects-list-page">
        <div className="header-banner">
          <div className="wall-container">
            <video className="bg-video" autoPlay muted loop playsInline>
              <source src={process.env.PUBLIC_URL + '/videos/drone-video.mp4'} />
            </video>
          </div>
          <h1>Ongoing Projects</h1>
        </div>

        <div className="projects-container">
          <div className="projects-header">
            <button onClick={handleBackClick} className="back-button">
              ← Back to Projects
            </button>
            <h2>Current Research Projects</h2>
            <p className="projects-count">{projects.length} active projects</p>
          </div>

          <div className="ongoing-projects-grid">
            {projects.map((project, index) => (
              <div key={index} className="project-tile">
                <div className="project-content">
                  <h3 className="project-title">{project.Title || 'Untitled Project'}</h3>
                  
                  {project.Objectives && (
                    <p className="project-description">{project.Objectives}</p>
                  )}

                  <div className="project-meta">
                    {project['Award Number'] && (
                      <div className="project-meta-item">
                        <span className="meta-label">Award Number:</span>
                        <span className="meta-value">{project['Award Number']}</span>
                      </div>
                    )}
                    
                    {project.Source && (
                      <div className="project-meta-item">
                        <span className="meta-label">Funding Source:</span>
                        <span className="meta-value">{project.Source}</span>
                      </div>
                    )}
                    
                    {project.Place && (
                      <div className="project-meta-item">
                        <span className="meta-label">Institution:</span>
                        <span className="meta-value">{project.Place}</span>
                      </div>
                    )}
                    
                    {project['Start Date'] && (
                      <div className="project-meta-item">
                        <span className="meta-label">Start Date:</span>
                        <span className="meta-value">{project['Start Date']}</span>
                      </div>
                    )}
                    
                    {project['End Date'] && (
                      <div className="project-meta-item">
                        <span className="meta-label">End Date:</span>
                        <span className="meta-value">{project['End Date']}</span>
                      </div>
                    )}
                    
                    {project['Person-Months'] && (
                      <div className="project-meta-item">
                        <span className="meta-label">Person-Months:</span>
                        <span className="meta-value">{project['Person-Months']}</span>
                      </div>
                    )}
                    
                    {project.Overlap && (
                      <div className="project-meta-item">
                        <span className="meta-label">Overlap:</span>
                        <span className="meta-value">{project.Overlap}</span>
                      </div>
                    )}

                    {project.People && (
                      <div className="project-meta-item">
                        <span className="meta-label">People:</span>
                        <div className="meta-value people-links">
                          {project.People.split(',').map((person, personIndex) => {
                            const trimmedPerson = person.trim();
                            const formattedName = trimmedPerson.replace(/\s+/g, "_").toLowerCase();
                            return (
                              <span key={personIndex}>
                                <a 
                                  href={`/team/${formattedName}`}
                                  className="person-link"
                                >
                                  {trimmedPerson}
                                </a>
                                {personIndex < project.People.split(',').length - 1 && ', '}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {project['Logo Source'] && (
                      <div className="project-meta-item logos-container">
                        <div className="meta-value">
                          {project['Logo Source'].split(',').map((logo, logoIndex) => (
                            <img 
                              key={logoIndex}
                              src={logo.trim().startsWith('http') ? logo.trim() : process.env.PUBLIC_URL + "/images/" + logo.trim()} 
                              alt={`${project.Title} logo ${logoIndex + 1}`}
                              className="logo-image"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {project.Link && (
                    <a 
                      href={project.Link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="project-link"
                    >
                      Learn More →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default OngoingProjectsList;
